import type {
  DataEditorRef,
  GridSelection,
  Item,
} from "@glideapps/glide-data-grid";
import type { Dispatch, RefObject, SetStateAction } from "react";
import type {
  ManualRowEditBuffer,
  ViewerEditContext,
} from "./manual-row-edit-buffer";
import { buildManualRowCells, type ManualRowSeed } from "./manual-row-utils";
import { emptySelection } from "./utils";
import {
  buildManualRowCreateEntry,
  buildManualRowDeleteEntry,
} from "./workbook-history-ops";

interface CreateManualRowActionsParams extends ViewerEditContext {
  canMutateActiveSheet: boolean;
  maxContentRow: number;
  maxContentCol: number;
  clearGridViewport: () => void;
  gridRef: RefObject<DataEditorRef | null>;
  setGridSelection: Dispatch<SetStateAction<GridSelection>>;
  buffer: Pick<ManualRowEditBuffer, "flushBufferedManualRowEdits">;
}

export const createManualRowActions = ({
  runId,
  history,
  mutations: { createManualRowAsync, deleteManualRow },
  activeSheet,
  bufferedManualRowEditsRef,
  recordWorkbookVersion,
  handleMutationError,
  canMutateActiveSheet,
  maxContentRow,
  maxContentCol,
  clearGridViewport,
  gridRef,
  setGridSelection,
  buffer: { flushBufferedManualRowEdits },
}: CreateManualRowActionsParams) => {
  const focusGridRow = (rowIndex: number, colIndex = 0) => {
    clearGridViewport();
    const cell: Item = [colIndex, rowIndex];
    setGridSelection({
      ...emptySelection,
      current: {
        cell,
        range: { x: colIndex, y: rowIndex, width: 1, height: 1 },
        rangeStack: [],
      },
    });
    requestAnimationFrame(() =>
      gridRef.current?.scrollTo(colIndex, rowIndex, "both", 40, 40, {
        hAlign: "center",
        vAlign: "center",
      }),
    );
  };

  const createManualRow = ({
    afterRow,
    seed,
  }: {
    afterRow?: number;
    seed?: ManualRowSeed | ManualRowSeed[];
  }) => {
    if (!canMutateActiveSheet || !activeSheet) return;

    const isInsert = afterRow != null;
    const rowIndex = afterRow ?? maxContentRow;
    const firstSeed = Array.isArray(seed) ? seed[0] : seed;
    const focusCol = firstSeed ? firstSeed.col - 1 : 0;
    const sheetId = activeSheet.id;
    const cells = buildManualRowCells(maxContentCol, seed);
    const anchor = isInsert ? { after_row: afterRow } : undefined;
    const optimisticToken = crypto.randomUUID();
    bufferedManualRowEditsRef.current.set(optimisticToken, {
      edits: new Map(),
      saving: false,
    });

    void createManualRowAsync({
      runId,
      sheetId,
      cells,
      anchor,
      optimisticToken,
    })
      .then((response) => {
        recordWorkbookVersion(response.version);
        history.push(
          buildManualRowCreateEntry({
            sheetId,
            rows: [{ cells, ...(anchor ? { anchor } : {}) }],
            manualRowIds: response.manual_row_ids ?? [],
            label: isInsert ? "Insert row" : undefined,
          }),
        );
        void flushBufferedManualRowEdits({
          token: optimisticToken,
          sheetId,
          manualRowId: response.manual_row_ids?.[0],
        });
      })
      .catch((mutationError: unknown) => {
        bufferedManualRowEditsRef.current.delete(optimisticToken);
        handleMutationError(
          isInsert ? "Failed to insert row" : "Failed to add row",
          mutationError,
        );
      });
    focusGridRow(rowIndex, focusCol);
  };

  const appendManualRow = (seed?: ManualRowSeed | ManualRowSeed[]) =>
    createManualRow({ seed });

  const insertManualRow = (afterRow: number) => createManualRow({ afterRow });

  const deleteManualRowById = (manualRowId: string | null | undefined) => {
    if (!activeSheet || !manualRowId) return;
    const sheetId = activeSheet.id;
    deleteManualRow(
      { runId, sheetId, manualRowId },
      {
        onSuccess: (response) => {
          recordWorkbookVersion(response.version);
          history.push(buildManualRowDeleteEntry({ sheetId, manualRowId }));
        },
        onError: (mutationError: unknown) =>
          handleMutationError("Failed to delete row", mutationError),
      },
    );
  };

  return {
    appendManualRow,
    insertManualRow,
    deleteManualRowById,
  };
};

export type ManualRowActions = ReturnType<typeof createManualRowActions>;
